/** Detecta el texto del enlace de política dentro del copy parametrizable (HU 8367). */
export const POLICY_LINK_PATTERN = /pol[ií]tica de cookies/i;

export const COOKIE_MODAL_QUOTE_CLASS = 'cookie-consent-modal__quote';
export const COOKIE_MODAL_ACCENT_CLASS = 'cookie-consent-modal__accent';

const ALLOWED_STRONG_CLASSES = new Set([
  COOKIE_MODAL_QUOTE_CLASS,
  COOKIE_MODAL_ACCENT_CLASS,
]);

const ALLOWED_INLINE_HTML_PATTERN =
  /<\/?(strong|b)(\s+class="cookie-consent-modal__(?:quote|accent)")?\s*>|<br\s*\/?>/i;

const CONSENT_PHRASE_PATTERN = /consentimiento para el uso de las cookies/i;

function stripInlineHtmlForMatching(text: string): string {
  return text
    .replace(/<\/?(strong|b)(?:\s[^>]*)?>/gi, '')
    .replace(/<br\s*\/?>/gi, ' ');
}

function wrapConsentPhrase(text: string): string {
  if (new RegExp(COOKIE_MODAL_ACCENT_CLASS, 'i').test(text)) {
    return text;
  }
  return text.replace(
    CONSENT_PHRASE_PATTERN,
    `<strong class="${COOKIE_MODAL_ACCENT_CLASS}">$&</strong>`
  );
}

function normalizeQuotedStrongLabels(text: string): string {
  return text
    .replace(
      /<strong(?:\s[^>]*)?>\s*Aceptar\s*<\/strong>/gi,
      `<strong class="${COOKIE_MODAL_QUOTE_CLASS}">Aceptar</strong>`
    )
    .replace(
      /<strong(?:\s[^>]*)?>\s*Personalizar\s*<\/strong>/gi,
      `<strong class="${COOKIE_MODAL_QUOTE_CLASS}">Personalizar</strong>`
    )
    .replace(
      /(clic en\s+)(Aceptar)(?=,)/gi,
      `$1<strong class="${COOKIE_MODAL_QUOTE_CLASS}">$2</strong>`
    )
    .replace(
      /(haga clic en\s+)(Personalizar)(?=\.)/gi,
      `$1<strong class="${COOKIE_MODAL_QUOTE_CLASS}">$2</strong>`
    );
}

function normalizeParagraphBreaks(text: string): string {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\n{2,}/g, '<br><br>')
    .replace(/(?:<br\s*\/?>\s*){2,}/gi, '<br><br>');
}

/** Corrige copy parametrizable y aplica clases semánticas para estilos del modal. */
export function normalizeCookieModalParamText(text: string | null | undefined): string {
  if (!text?.trim()) {
    return '';
  }

  let normalized = normalizeParagraphBreaks(text)
    .replace(
      new RegExp(
        `<strong(?:\\s[^>]*)?>\\s*\\?\\s*([^?]+?)\\s*\\?\\s*<\\/strong>`,
        'gi'
      ),
      `<strong class="${COOKIE_MODAL_QUOTE_CLASS}">$1</strong>`
    )
    .replace(
      /<strong(?:\s[^>]*)?>\s*[\u201C\u201D"\u00AB\u00BB\u2018\u2019']\s*([^<]+?)\s*[\u201C\u201D"\u00AB\u00BB\u2018\u2019']\s*<\/strong>/gi,
      `<strong class="${COOKIE_MODAL_QUOTE_CLASS}">$1</strong>`
    );

  normalized = normalizeQuotedStrongLabels(normalized);
  normalized = wrapConsentPhrase(normalized);

  return normalized;
}

function decodeBasicHtmlEntities(text: string): string {
  return text
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
}

const STRONG_TAG_PATTERN =
  /&lt;(\/?)\s*(strong|b)(?:\s+class=(?:&quot;|&#39;|')(cookie-consent-modal__(?:quote|accent))(?:&quot;|&#39;|'))?\s*&gt;/gi;

export function sanitizeCookieModalHtml(raw: string | null | undefined): string {
  let text = String(raw ?? '');
  if (!text) {
    return '';
  }
  if (/&lt;|&gt;|&quot;|&#39;|&amp;lt;/i.test(text)) {
    text = decodeBasicHtmlEntities(text);
  }
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  return escaped
    .replace(STRONG_TAG_PATTERN, (_match, slash: string, tag: string, className?: string) => {
      if (slash) {
        return `</${tag}>`;
      }
      if (className && ALLOWED_STRONG_CLASSES.has(className)) {
        return `<${tag} class="${className}">`;
      }
      return `<${tag}>`;
    })
    .replace(/&lt;br\s*\/?&gt;/gi, '<br>');
}

export function containsCookieModalMarkup(text: string): boolean {
  return ALLOWED_INLINE_HTML_PATTERN.test(text);
}

export interface PolicyLinkMatch {
  index: number;
  label: string;
}

export function findPolicyLinkInText(text: string): PolicyLinkMatch | null {
  if (!text?.trim()) {
    return null;
  }
  const match = stripInlineHtmlForMatching(text).match(POLICY_LINK_PATTERN);
  if (!match || match.index == null) {
    return null;
  }
  return { index: match.index, label: match[0] };
}

export interface ModalTextSegment {
  text: string;
  isPolicyLink: boolean;
}

function buildPlainCharToHtmlIndexMap(html: string): number[] {
  const map: number[] = [];
  let inTag = false;

  for (let index = 0; index < html.length; index++) {
    const char = html[index];
    if (char === '<') {
      inTag = true;
      continue;
    }
    if (char === '>') {
      inTag = false;
      continue;
    }
    if (!inTag) {
      map.push(index);
    }
  }

  return map;
}

function splitParagraphIntoSegments(paragraph: string): ModalTextSegment[] {
  const link = findPolicyLinkInText(paragraph);
  if (!link) {
    return [{ text: paragraph, isPolicyLink: false }];
  }

  const plainToHtml = buildPlainCharToHtmlIndexMap(paragraph);
  const linkStartPlain = link.index;
  const linkEndPlain = link.index + link.label.length;
  const segments: ModalTextSegment[] = [];

  if (linkStartPlain > 0) {
    segments.push({
      text: paragraph.slice(0, plainToHtml[linkStartPlain]),
      isPolicyLink: false,
    });
  }

  segments.push({ text: link.label, isPolicyLink: true });

  if (linkEndPlain < plainToHtml.length) {
    segments.push({
      text: paragraph.slice(plainToHtml[linkEndPlain]),
      isPolicyLink: false,
    });
  }

  return segments;
}

/** Parte el texto del modal preservando el enlace de política como segmento clickeable. */
export function buildCookieModalTextSegments(text: string): ModalTextSegment[] {
  const segments: ModalTextSegment[] = [];
  const normalized = normalizeCookieModalParamText(text);
  const paragraphs = normalized
    .split(/(?:<br\s*\/?>\s*){2,}|\n\n+/gi)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph.length > 0);

  paragraphs.forEach((paragraph, paragraphIndex) => {
    segments.push(...splitParagraphIntoSegments(paragraph));
    if (paragraphIndex < paragraphs.length - 1) {
      segments.push({ text: '\n\n', isPolicyLink: false });
    }
  });

  return segments;
}
